import { useCallback, useEffect, useState } from 'react';
import MyTeamModal from './MyTeamModal';
import { searchPlayers } from '../api/myTeamApi';
import type { IPlayerSearchResult } from '../types/myTeam';

const DEBOUNCE_MS = 300;

interface SearchPlayerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddPlayers: (players: IPlayerSearchResult[]) => void | Promise<void>;
}

function SearchPlayerModal({ open, onOpenChange, onAddPlayers }: SearchPlayerModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<IPlayerSearchResult[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const timer = setTimeout(() => {
      setLoading(true);
      searchPlayers(query)
        .then((list) => setResults(list))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const toggleSelect = useCallback((playerId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) next.delete(playerId);
      else next.add(playerId);
      return next;
    });
  }, []);

  const handleAdd = useCallback(async () => {
    const toAdd = results.filter((p) => selected.has(p.playerId));
    if (toAdd.length === 0) return;
    await onAddPlayers(toAdd);
    setQuery('');
    setSelected(new Set());
    setResults([]);
  }, [results, selected, onAddPlayers]);

  return (
    <MyTeamModal
      open={open}
      onOpenChange={onOpenChange}
      title="Search Player"
      className="search-player-dialog"
      footer={
        <div className="search-player-footer">
          <button
            type="button"
            className="search-player-add-btn"
            onClick={handleAdd}
            disabled={selected.size === 0 || loading || submitting}
          >
            {submitting ? 'Adding...' : loading ? 'Searching...' : 'Add Player'}
          </button>
        </div>
      }
    >
      <div className="search-player-modal">
        <div className="search-player-input-row">
          <input
            type="text"
            placeholder="Player ID Or Player Name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="search-player-input"
          />
        </div>
        {loading && results.length === 0 && query.trim() && (
          <p className="search-player-loading">Searching...</p>
        )}
        {!loading && results.length > 0 && (
          <div className="search-player-results">
            {results.map((player) => (
              <div
                key={player.playerId}
                className="search-player-result-row"
                onClick={() => toggleSelect(player.playerId)}
                onKeyDown={(e) => e.key === 'Enter' && toggleSelect(player.playerId)}
                role="button"
                tabIndex={0}
              >
                <span className="search-player-result-name">
                  {player.playerFirstName} {player.playerLastName}
                </span>
                <label className="search-player-checkbox" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.has(player.playerId)}
                    onChange={() => toggleSelect(player.playerId)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </label>
              </div>
            ))}
          </div>
        )}
        {!loading && query.trim() && results.length === 0 && (
          <p className="search-player-no-results">No players found.</p>
        )}
      </div>
    </MyTeamModal>
  );
}

export default SearchPlayerModal;
